import { Suspense, useEffect, useLayoutEffect, useRef } from 'react';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { useTheme, resolveDark, applyTheme } from '@/store/useTheme';
import { useSettings, useDataLoaded } from '@/store/selectors';
import { getAppDestination } from '@/redesign/home/firstRun';
import { useAuth } from '@/contexts/AuthContext';
import { UiLanguageSync } from '@/components/UiLanguageSync';
import { DataLoader } from '@/components/DataLoader';
import ErrorBoundary from '@/components/ErrorBoundary';
import RouteErrorBoundary from '@/components/RouteErrorBoundary';
import { captureAttribution, getStoredAttribution } from '@/utils/attribution';
import { APPROVED_CAMPAIGN_LABELS } from '@/utils/campaignLabels';
import { PilotTryEntry } from '@/utils/pilotTryEntry';
import { supabase } from '@/lib/supabase';
import {
  handoffReceiptedSignup,
  isVerifiedSoleEmail,
  revokeForVerifiedAccount,
  revokeStoredSignupJourney,
} from '@/services/signupJourney';
import {
  beginPosthogAuthVerification,
  captureConsentedPageview,
  captureProductEvent,
  completeVerifiedSignup,
  isPosthogConfigured,
  noteConsentedFirstSeen,
  posthogConfigurationIssue,
  setAnonymousAnalyticsUser,
  setVerifiedAnalyticsUser,
  syncPosthogConsent,
} from '@/services/posthogAnalytics';
import {
  ANALYTICS_MARKER_KEY,
  isVerifiedSoleGoogle,
  loadVerifiedAnalyticsUser,
} from '@/services/verifiedGoogleSignup';
import { useConsent } from '@/hooks/useConsent';
import CookieConsent from '@/components/CookieConsent';
import { NativeSplashHide } from '@/lib/native/NativeSplashHide';
import { publicGrammarPath } from '@/utils/publicGrammarPath';
import { FLAGS } from '@/config/featureFlags';

// The redesign is the only design (flag-flip PR, bd Vidi-tui): every route
// renders its "Words with a place to live" screen directly — no flag, no
// gates, no legacy screens. Chunk names still start with "Design" (that is
// how the ports named them); the names are historical, not a flag signal.
const DesignLandingGate = lazyWithRetry(() => import('@/redesign/landing/DesignLandingGate'));
const DesignReviewGate = lazyWithRetry(() => import('@/redesign/review/DesignReviewGate'));
const DesignHomeGate = lazyWithRetry(() => import('@/redesign/home/DesignHomeGate'));
const DesignFeedbackGate = lazyWithRetry(() => import('@/redesign/feedback/DesignFeedbackGate'));
const DesignCreateGate = lazyWithRetry(() => import('@/redesign/create/DesignCreateGate'));
const DesignLibraryGate = lazyWithRetry(() => import('@/redesign/library/DesignLibraryGate'));
const DesignSettingsGate = lazyWithRetry(() => import('@/redesign/settings/DesignSettingsGate'));
const DesignStatsGate = lazyWithRetry(() => import('@/redesign/stats/DesignStatsGate'));
const DesignAchievementsGate = lazyWithRetry(
  () => import('@/redesign/stats/DesignAchievementsGate'),
);
const DesignWordPackGate = lazyWithRetry(() => import('@/redesign/wordpack/DesignWordPackGate'));
const DesignAuthGate = lazyWithRetry(() => import('@/redesign/auth/DesignAuthGate'));
const DesignTryGate = lazyWithRetry(() => import('@/redesign/try/DesignTryGate'));
const DesignCreateSketchGate = lazyWithRetry(
  () => import('@/redesign/create-sketch/CreateSketchGate'),
);
const DesignOnboardingGate = lazyWithRetry(
  () => import('@/redesign/onboarding/DesignOnboardingGate'),
);
const DesignGuidesGate = lazyWithRetry(() => import('@/redesign/guides/DesignGuidesGate'));
const DesignChangelogGate = lazyWithRetry(() => import('@/redesign/meta/DesignChangelogGate'));
const DesignNotFoundGate = lazyWithRetry(() => import('@/redesign/meta/DesignNotFoundGate'));
const DesignEditorialGate = lazyWithRetry(() => import('@/redesign/editorial/DesignEditorialGate'));

// Legacy-system pages kept by decision (never-delete list, bd Vidi-tui):
// the three admin dashboards and founding-feedback still render on the old
// token system inside the old Layout chrome.
const FoundingFeedback = lazyWithRetry(() => import('@/pages/FoundingFeedback'));
const AdminFounding = lazyWithRetry(() => import('@/pages/AdminFounding'));
const AdminProductFeedback = lazyWithRetry(() => import('@/pages/AdminProductFeedback'));
const AdminUsage = lazyWithRetry(() => import('@/pages/AdminUsage'));
const AdminGrowth = lazyWithRetry(() => import('@/pages/AdminGrowth'));
const LegacyLayout = lazyWithRetry(() =>
  import('@/components/layout/Layout').then((module) => ({ default: module.Layout })),
);

function LazyFallback() {
  return (
    <div
      className="min-h-dvh flex items-center justify-center"
      style={{ backgroundColor: 'var(--bg)' }}
    >
      <div className="animate-pulse text-lg" style={{ color: 'var(--text-secondary)' }}>
        Loading...
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div
        className="min-h-dvh flex items-center justify-center"
        style={{ backgroundColor: 'var(--bg)' }}
      >
        <div className="animate-pulse text-lg" style={{ color: 'var(--text-secondary)' }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public route — shows landing page to guests, redirects authenticated users to app
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div
        className="min-h-dvh flex items-center justify-center"
        style={{ backgroundColor: 'var(--bg)' }}
      >
        <div className="animate-pulse text-lg" style={{ color: 'var(--text-secondary)' }}>
          Loading...
        </div>
      </div>
    );
  }

  // Redirect to /app which goes through DataLoader first,
  // ensuring settings are loaded before deciding home vs onboarding
  if (user) {
    return <Navigate to="/app" replace />;
  }

  return <>{children}</>;
}

/** Reads settings from store AFTER DataLoader has loaded, then redirects */
function AppRedirect() {
  const settings = useSettings();
  const dataLoaded = useDataLoaded();
  if (!dataLoaded) return null;
  const destination = getAppDestination(settings.onboardingCompleted);
  return <Navigate to={destination} replace />;
}

/** Guard that redirects to onboarding if it hasn't been completed yet */
function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const settings = useSettings();
  const dataLoaded = useDataLoaded();
  const location = useLocation();

  // Don't redirect while data is still loading, or if already on onboarding
  if (!dataLoaded || location.pathname === '/onboarding') {
    return <>{children}</>;
  }

  if (!settings.onboardingCompleted) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

/**
 * /guides — signed-in visitors keep the in-app shelf (quizzes, TTS, ear
 * training). Logged-out visitors used to hit ProtectedRoute's silent login
 * wall; send them to the public grammar library instead (issue #128).
 * Full `window.location` navigation is required so Vercel's /learn/grammar
 * rewrite runs — a React Router <Navigate> would 404 the SPA catch-all.
 */
function GuidesRoute() {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Native: there is no Vercel rewrite to land on, so stay on the in-app
    // shelf below instead of replacing the page.
    if (isLoading || user || FLAGS.nativeIOS) return;
    window.location.replace(publicGrammarPath(location.search));
  }, [isLoading, user, location.search]);

  if (isLoading) {
    return <LazyFallback />;
  }

  if (!user && FLAGS.nativeIOS) {
    return <DesignGuidesGate />;
  }

  if (!user) {
    return <LazyFallback />;
  }

  return (
    <DataLoader>
      <OnboardingGuard>
        <RouteErrorBoundary>
          <DesignGuidesGate />
        </RouteErrorBoundary>
      </OnboardingGuard>
    </DataLoader>
  );
}

/**
 * /word-pack — signed-in visitors get the import machine (DataLoader +
 * onboarding). Logged-out visitors get a public index of the static packs
 * so the footer link is not a dead login wall (issue #128).
 */
function WordPackRoute() {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return <LazyFallback />;
  }
  if (user) {
    return (
      <DataLoader>
        <OnboardingGuard>
          <RouteErrorBoundary>
            <DesignWordPackGate />
          </RouteErrorBoundary>
        </OnboardingGuard>
      </DataLoader>
    );
  }
  return <DesignWordPackGate />;
}

/**
 * Changelog — public since the meta port (bd Vidi-hee, IA gap #3). Signed-in
 * users keep the old load ordering (DataLoader → OnboardingGuard → screen)
 * that the protected group provided; anonymous visitors load nothing (the
 * changelog reads no store data). Wait for auth to resolve before choosing a
 * branch — without it a signed-in visitor flashes the anonymous view on a
 * cold load (review finding).
 */
function ChangelogRoute() {
  const { user, isLoading } = useAuth();
  if (isLoading) {
    return (
      <div
        className="min-h-dvh flex items-center justify-center"
        style={{ backgroundColor: 'var(--bg)' }}
      >
        <div className="animate-pulse text-lg" style={{ color: 'var(--text-secondary)' }}>
          Loading...
        </div>
      </div>
    );
  }
  if (user) {
    return (
      <DataLoader>
        <OnboardingGuard>
          <DesignChangelogGate />
        </OnboardingGuard>
      </DataLoader>
    );
  }
  return <DesignChangelogGate />;
}

function AppContent() {
  const { theme } = useTheme();
  const settings = useSettings();
  const { user } = useAuth();
  const { i18n } = useTranslation();

  // Keep the document theme class in sync with the active theme setting.
  // System preference changes are also handled by useTheme's matchMedia listener;
  // this effect ensures light/dark toggles repaint immediately.
  useEffect(() => {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const isDark = resolveDark(theme, prefersDark);
    applyTheme(isDark ? 'dark' : 'light');
  }, [theme]);

  // Sync <html lang> attribute with active i18n locale (accessibility + SEO)
  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <>
      <UiLanguageSync
        currentLanguagePair={settings.currentLanguagePair}
        immersionMode={settings.immersionMode}
        signedIn={Boolean(user)}
      />
      <Routes>
        {/* Landing page — public, redirects authenticated users to app */}
        <Route
          path="/"
          element={
            <PublicRoute>
              <PilotTryEntry>
                <DesignLandingGate />
              </PilotTryEntry>
            </PublicRoute>
          }
        />

        {/* Auth routes — redirect to app if already signed in */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <DesignAuthGate />
            </PublicRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <DesignAuthGate />
            </PublicRoute>
          }
        />
        <Route path="/forgot-password" element={<DesignAuthGate />} />
        <Route path="/reset-password" element={<DesignAuthGate />} />
        <Route
          path="/try"
          element={
            <Suspense fallback={<LazyFallback />}>
              <DesignTryGate />
            </Suspense>
          }
        />
        {/* Preview-only Create mock. 404s on vidilearn.com (CreateSketchGate). */}
        <Route
          path="/create-sketch"
          element={
            <Suspense fallback={<LazyFallback />}>
              <DesignCreateSketchGate />
            </Suspense>
          }
        />
        <Route path="/about" element={<DesignEditorialGate />} />
        <Route path="/how-it-works" element={<DesignEditorialGate />} />
        <Route path="/features" element={<DesignEditorialGate />} />
        <Route path="/faq" element={<DesignEditorialGate />} />
        <Route path="/contact" element={<DesignEditorialGate />} />
        <Route path="/pricing" element={<DesignEditorialGate />} />
        <Route path="/grammar-guides" element={<Navigate to="/guides" replace />} />
        <Route path="/guides" element={<GuidesRoute />} />
        <Route path="/word-packs" element={<Navigate to="/word-pack" replace />} />
        <Route path="/word-pack" element={<WordPackRoute />} />
        <Route path="/impressum" element={<DesignEditorialGate />} />
        <Route path="/privacy" element={<DesignEditorialGate />} />
        <Route path="/terms" element={<DesignEditorialGate />} />
        {/* Changelog — public since the meta port (bd Vidi-hee, IA gap #3) */}
        <Route path="/changelog" element={<ChangelogRoute />} />

        {/* Protected app routes — single ProtectedRoute + DataLoader wrapper
          prevents re-mounting on navigation between pages */}
        <Route
          element={
            <ProtectedRoute>
              <Outlet />
            </ProtectedRoute>
          }
        >
          <Route element={<DataLoader />}>
            {/* /app redirect — uses AppRedirect which reads settings AFTER DataLoader has loaded */}
            <Route path="app" element={<AppRedirect />} />

            {/* /dashboard — alias for /home (bookmarks, muscle memory) */}
            <Route path="dashboard" element={<Navigate to="/home" replace />} />

            {/* Full-screen pages (no Layout sidebar/nav) */}
            <Route
              path="feedback"
              element={
                <OnboardingGuard>
                  <DesignFeedbackGate />
                </OnboardingGuard>
              }
            />
            <Route path="onboarding" element={<DesignOnboardingGate />} />
            <Route
              path="home"
              element={
                <RouteErrorBoundary>
                  <OnboardingGuard>
                    <DesignHomeGate />
                  </OnboardingGuard>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="review"
              element={
                <RouteErrorBoundary>
                  <DesignReviewGate />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="review/:deckId"
              element={
                <RouteErrorBoundary>
                  <DesignReviewGate />
                </RouteErrorBoundary>
              }
            />
            <Route
              path="library"
              element={
                <RouteErrorBoundary>
                  <OnboardingGuard>
                    <DesignLibraryGate />
                  </OnboardingGuard>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="create"
              element={
                <RouteErrorBoundary>
                  <OnboardingGuard>
                    <DesignCreateGate />
                  </OnboardingGuard>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="settings"
              element={
                <RouteErrorBoundary>
                  <OnboardingGuard>
                    <DesignSettingsGate />
                  </OnboardingGuard>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="stats"
              element={
                <RouteErrorBoundary>
                  <OnboardingGuard>
                    <DesignStatsGate />
                  </OnboardingGuard>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="achievements"
              element={
                <RouteErrorBoundary>
                  <OnboardingGuard>
                    <DesignAchievementsGate />
                  </OnboardingGuard>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="ear-training"
              element={
                <RouteErrorBoundary>
                  <OnboardingGuard>
                    <DesignGuidesGate />
                  </OnboardingGuard>
                </RouteErrorBoundary>
              }
            />
            <Route
              path="tutor"
              element={
                <RouteErrorBoundary>
                  <OnboardingGuard>
                    <DesignGuidesGate />
                  </OnboardingGuard>
                </RouteErrorBoundary>
              }
            />

            {/* Legacy-system islands (never-delete list, bd Vidi-tui): the old
              Layout chrome and token system stay for these four routes. */}
            <Route
              element={
                <OnboardingGuard>
                  <Suspense fallback={<LazyFallback />}>
                    <LegacyLayout />
                  </Suspense>
                </OnboardingGuard>
              }
            >
              <Route path="founding-feedback" element={<FoundingFeedback />} />
              <Route path="admin/founding" element={<AdminFounding />} />
              <Route path="admin/feedback" element={<AdminProductFeedback />} />
              <Route path="admin/usage" element={<AdminUsage />} />
              <Route path="admin/growth" element={<AdminGrowth />} />
            </Route>
          </Route>
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<DesignNotFoundGate />} />
      </Routes>
    </>
  );
}

function PublicJourneyStep() {
  const { pathname } = useLocation();
  const { consent } = useConsent();
  const { user } = useAuth();
  useEffect(() => {
    captureConsentedPageview(pathname);
  }, [pathname, consent?.analytics, user?.id]);
  useEffect(() => {
    const steps: Record<string, 'entry' | 'how_it_works' | 'try' | 'signup'> = {
      '/': 'entry',
      '/how-it-works': 'how_it_works',
      '/try': 'try',
      '/signup': 'signup',
    };
    const step = steps[pathname];
    if (step) captureProductEvent('journey_step', { step });
  }, [pathname, consent?.analytics]);
  return null;
}

function safeCampaignPart(value: string | null, allowed: readonly string[]): string {
  return value && allowed.includes(value.toLowerCase()) ? value.toLowerCase() : 'unknown';
}

function App() {
  // Capture UTM attribution on first visit, and again when the cookie banner is
  // answered: the banner appears after load, so the first call only snapshots
  // the landing URL in memory and the decision is what releases it.
  const { consent } = useConsent();
  const { user, session, isLoading } = useAuth();
  const analyticsConsent = consent?.analytics ?? null;
  const authUserId = user?.id ?? null;
  const verificationTicket = useRef(0);
  const sessionEntryRef = useRef<string | null>(null);
  const sessionSurface = () => {
    const path = window.location.pathname;
    if (path.startsWith('/create')) return 'create' as const;
    if (path.startsWith('/library')) return 'library' as const;
    if (path.startsWith('/review')) return 'review' as const;
    if (path === '/' || path.startsWith('/how-it-works') || path.startsWith('/try'))
      return 'public' as const;
    return 'other' as const;
  };
  // Close the capture gate before child effects or async card outcomes can run.
  useLayoutEffect(() => {
    if (analyticsConsent === true && isPosthogConfigured())
      verificationTicket.current = beginPosthogAuthVerification(authUserId);
  }, [analyticsConsent, isLoading, authUserId, session?.access_token]);
  useEffect(() => {
    captureAttribution();
    const configIssue = posthogConfigurationIssue();
    if (configIssue) console.warn(configIssue);
    syncPosthogConsent(analyticsConsent === true);
    // Best-effort withdrawal of the optional signup context stored for this browser's signup. A no-op
    // unless a capability was kept under an earlier consent; a withdrawal whose delivery failed is
    // retried on later loads and consent changes (even after consent is granted again) until it lands.
    void revokeStoredSignupJourney((fn, args) => supabase.rpc(fn, args), analyticsConsent === true);
    if (analyticsConsent !== true || !isPosthogConfigured()) return;
    const firstTouch = getStoredAttribution();
    if (!firstTouch) return;
    noteConsentedFirstSeen(firstTouch.first_seen_at);
    captureProductEvent(
      'campaign_visit',
      {
        source: safeCampaignPart(firstTouch.utm_source, APPROVED_CAMPAIGN_LABELS.utm_source),
        medium: safeCampaignPart(firstTouch.utm_medium, APPROVED_CAMPAIGN_LABELS.utm_medium),
        campaign: safeCampaignPart(firstTouch.utm_campaign, APPROVED_CAMPAIGN_LABELS.utm_campaign),
        placement: safeCampaignPart(
          firstTouch.utm_content,
          APPROVED_CAMPAIGN_LABELS.utm_content,
        ) as
          | 'alphaandbetausers'
          | 'betatests'
          | 'betatesters'
          | 'testmyapp'
          | 'germanlearning_resources'
          | 'languagelearning_resources'
          | 'unknown',
        referrer_domain: safeCampaignPart(firstTouch.referrer_domain, [
          'reddit.com',
          'www.reddit.com',
          'google.com',
          'www.google.com',
        ]),
      },
      firstTouch.anonymous_id,
    );
  }, [analyticsConsent]);

  useEffect(() => {
    if (analyticsConsent !== true || !isPosthogConfigured() || isLoading) return;
    if (!authUserId) {
      setAnonymousAnalyticsUser(verificationTicket.current);
      if (sessionEntryRef.current !== 'anonymous') {
        sessionEntryRef.current = 'anonymous';
        captureProductEvent('session_entry', { state: 'anonymous', surface: sessionSurface() });
      }
      return;
    }
    let active = true;
    const ticket = verificationTicket.current;
    void loadVerifiedAnalyticsUser(authUserId, {
      getUser: () => supabase.auth.getUser(),
      classify: async () => await supabase.rpc('classify_trusted_google_signup'),
      isCurrent: () => active && ticket === verificationTicket.current,
    })
      .then((verified) => {
        // A newer verification (consent or token change) supersedes this one.
        if (!active || ticket !== verificationTicket.current) return;
        // A stored signup journey belongs to the account that signed up in this browser. Another
        // verified account, or an identity that could not be established, revokes it (conservatively).
        void revokeForVerifiedAccount((fn, args) => supabase.rpc(fn, args), verified?.id);
        if (!verified) return;
        setVerifiedAnalyticsUser(
          verified.id,
          verified.app_metadata?.[ANALYTICS_MARKER_KEY],
          ticket,
        );
        if (sessionEntryRef.current !== verified.id) {
          sessionEntryRef.current = verified.id;
          captureProductEvent(
            'session_entry',
            { state: 'signed_in', surface: sessionSurface() },
            undefined,
            verified.id,
          );
        }
        completeVerifiedSignup(
          verified.id,
          verified.created_at,
          isVerifiedSoleGoogle(verified),
          isVerifiedSoleEmail(verified),
        );
        // Email signups are counted only by the server receipt claim (also when this is a fresh
        // browser). Optional and never awaited: sign-in and draft recovery do not depend on it.
        void handoffReceiptedSignup(
          (fn, args) => supabase.rpc(fn, args),
          verified,
          isVerifiedSoleEmail(verified),
        );
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [analyticsConsent, isLoading, authUserId, session?.access_token]);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <PublicJourneyStep />
        <Suspense fallback={<LazyFallback />}>
          <AppContent />
        </Suspense>
      </BrowserRouter>
      {!FLAGS.nativeIOS && (
        // Both load same-origin /_vercel/* scripts, which 404 on every
        // launch inside the native shell.
        <>
          <Analytics
            beforeSend={(event) => {
              // Strip admin paths from analytics for privacy
              if (event.url.includes('/admin')) return null;
              return event;
            }}
          />
          <SpeedInsights />
        </>
      )}
      <CookieConsent />
      <NativeSplashHide />
    </ErrorBoundary>
  );
}

export default App;
